import React, { useState, useEffect } from "react";
import { categoryAPI } from "../api/apiService";
import { motion } from "framer-motion";
import { Plus, Edit2, Trash2, Search, Package } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useContext } from "react";
import { NotificationContext } from "../context/NotificationContext";
import EmptyState from "../components/EmptyState";

export default function CategoryManagementPage() {
  const { userRole } = useAuth();
  const { addNotification } = useContext(NotificationContext);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editing, setEditing] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: "",
    parent: ""
  });
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Fetch categories from API
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAllCategories();
      const categoriesData = response.data?.results || response.data || [];
      setCategories(categoriesData);
    } catch (error) {
      // Fallback empty or error notification could be added here
      addNotification("error", "Error", "Failed to load categories");
    }
  };


  async function addCategory() {
    const isSub = newCategory.parent !== "";
    if (!newCategory.name.trim()) {
      addNotification("error", "Validation Error", `${isSub ? "Subcategory" : "Category"} name is required`);
      return;
    }

    try {
      // Map 'parent' string to actual ID or null
      const payload = {
        name: newCategory.name.trim(),
        parent: newCategory.parent === "" ? null : parseInt(newCategory.parent)
      };
      const response = await categoryAPI.createCategory(payload);
      if (response.data) {
        await fetchCategories();
        addNotification("product", `${isSub ? "Subcategory" : "Category"} Added`, `${newCategory.name} ${isSub ? "subcategory" : "category"} added successfully`);
        setNewCategory({ name: "", parent: "" });
        setShowAddModal(false);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.name?.[0] || `Failed to add ${isSub ? "subcategory" : "category"}`;
      addNotification("error", "Error", errorMsg);
    }
  }

  async function updateCategory(updated) {
    const isSub = updated.parent !== "" && updated.parent !== null && updated.parent !== undefined;
    if (!updated.name?.trim()) {
      addNotification("error", "Validation Error", `${isSub ? "Subcategory" : "Category"} name is required`);
      return;
    }
    // Only send editable fields
    const payload = {
      name: updated.name.trim(),
      parent: updated.parent === "" ? null : (updated.parent?.id || updated.parent)
    };
    try {
      const response = await categoryAPI.updateCategory(updated.id, payload);
      if (response.data) {
        await fetchCategories();
        addNotification("product", `${isSub ? "Subcategory" : "Category"} Updated`, `${updated.name} ${isSub ? "subcategory" : "category"} updated successfully`);
        setEditing(null);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.name?.[0] || `Failed to update ${isSub ? "subcategory" : "category"}`;
      addNotification("error", "Error", errorMsg);
    }
  }

  async function performDelete(id) {
    const category = categories.find(c => c.id === id);
    try {
      await categoryAPI.deleteCategory(id);
      setCategories(categories.filter(cat => cat.id !== id));
      addNotification("product", "Category Deleted", `${category?.name} category has been removed`);
      setCategoryToDelete(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete category";
      addNotification("error", "Error", errorMsg);
      setCategoryToDelete(null);
    }
  }

  return (
    <div className="min-h-screen bg-light dark:bg-dark-bg p-4 md:p-8 transition-colors">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Product Categories</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">Manage your product categories</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-bold transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Category
          </motion.button>
        </div>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-2 bg-white dark:bg-dark-card rounded-lg px-4 py-3 border border-gray-200 dark:border-dark-border shadow-sm">
          <Search className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-500 flex-1"
          />
        </div>
      </motion.div>

      {/* Categories Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-8 pb-20"
      >
        {/* Helper to check if a category or its children match the search */}
        {categories.filter(c => !c.parent).map((mainCat, idx) => {
          const subCatMap = new Map();
          (mainCat.subcategories || []).forEach(sc => subCatMap.set(String(sc.id), sc));
          categories.forEach(c => {
            if (c.parent) {
              const parentId = typeof c.parent === 'object' ? c.parent?.id : c.parent;
              if (String(parentId) === String(mainCat.id)) {
                subCatMap.set(String(c.id), c);
              }
            }
          });
          const subCats = Array.from(subCatMap.values());
          
          const mainMatches = mainCat.name.toLowerCase().includes(searchTerm.toLowerCase());
          const matchingSubs = subCats.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));
          
          if (!mainMatches && matchingSubs.length === 0) return null;

          return (
            <div key={mainCat.id} className="space-y-4">
              {/* Main Category Card */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`p-6 rounded-2xl bg-white dark:bg-dark-card border-2 shadow-md transition-all ${mainMatches ? 'border-blue-400 ring-4 ring-blue-50' : 'border-blue-100'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center text-blue-600">
                      <Package className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">{mainCat.name}</h3>
                      <p className="text-xs font-bold text-gray-400 mt-0.5">Main Category • {subCats.length} Subcategories</p>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <button 
                      onClick={() => {
                        setNewCategory({ name: "", parent: mainCat.id });
                        setShowAddModal(true);
                      }}
                      className="px-3 py-2 text-xs font-bold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center gap-1.5 shadow-sm"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Subcategory
                    </button>
                    <div className="h-8 w-[1px] bg-gray-200 dark:bg-dark-border mx-1" />
                    <button onClick={() => setEditing(mainCat)} className="p-2.5 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-600 hover:text-white transition-all">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => setCategoryToDelete(mainCat)} className="p-2.5 text-red-600 bg-red-50 rounded-lg hover:bg-red-600 hover:text-white transition-all">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Subcategories Grid */}
              {(mainMatches ? subCats : matchingSubs).length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pl-12">
                  {(mainMatches ? subCats : matchingSubs).map(sub => (
                    <motion.div 
                      key={sub.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className={`p-4 rounded-xl flex items-center justify-between hover:border-blue-400 transition-all group ${sub.name.toLowerCase().includes(searchTerm.toLowerCase()) && searchTerm ? 'bg-blue-50 border-2 border-blue-400' : 'bg-gray-50/50 dark:bg-dark-bg/50 border border-dashed border-gray-200 dark:border-dark-border'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${sub.name.toLowerCase().includes(searchTerm.toLowerCase()) && searchTerm ? 'bg-blue-600' : 'bg-blue-400'}`} />
                        <span className={`font-bold uppercase text-sm transition-colors ${sub.name.toLowerCase().includes(searchTerm.toLowerCase()) && searchTerm ? 'text-blue-700' : 'text-gray-700 dark:text-gray-300 group-hover:text-blue-600'}`}>
                          {sub.name}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setEditing(sub)} className="p-1.5 text-blue-600 hover:scale-110"><Edit2 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setCategoryToDelete(sub)} className="p-1.5 text-red-600 hover:scale-110"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Categories with no parent and no children (Edge Case or Uncategorized Main) */}
        {categories.length > 0 && categories.filter(c => !c.parent && !categories.some(child => String(child.parent?.id || child.parent) === String(c.id))).length === 0 && searchTerm && !categories.some(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())) && (
            <div className="text-center py-12">
               <Package className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
               <p className="text-gray-600 dark:text-gray-400 font-medium">No categories found matching "{searchTerm}"</p>
            </div>
        )}
      </motion.div>

      {/* Empty State when everything is filtered out */}
      {categories.length > 0 && !categories.some(c => !c.parent && (c.name.toLowerCase().includes(searchTerm.toLowerCase()) || categories.some(child => String(child.parent?.id || child.parent) === String(c.id) && child.name.toLowerCase().includes(searchTerm.toLowerCase())))) && (
        <EmptyState
          preset="category"
          title={`No matches found for "${searchTerm}"`}
          message="Try searching for a different category name."
          className="py-8"
        />
      )}

      {categories.length === 0 && (
        <EmptyState
          preset="category"
          title="No categories created yet"
          message="Click 'Add Category' to get started."
          className="py-8"
        />
      )}

      {/* Edit Modal */}
      {editing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setEditing(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-dark-card rounded-lg p-8 max-w-md w-full shadow-xl"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {editing.parent ? "Edit Subcategory" : "Edit Category"}
            </h2>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  {editing.parent ? "Subcategory Name" : "Category Name"}
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:border-blue-500 transition-all font-bold"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Parent Category (Optional)</label>
                <select
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:border-blue-500 transition-all font-bold"
                  value={editing.parent?.id || editing.parent || ""}
                  onChange={(e) => setEditing({ ...editing, parent: e.target.value })}
                >
                  <option value="">None (Main Category)</option>
                  {categories.filter(c => !c.parent && c.id !== editing.id).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400 mt-2 font-bold px-1 uppercase tracking-wider italic">Changing parent will move this category and all its products</p>
              </div>
            </div>
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => updateCategory(editing)}
                className="flex-1 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-bold transition-colors"
              >
                Save Changes
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setEditing(null)}
                className="flex-1 py-3 rounded-lg border-2 border-gray-300 dark:border-dark-border text-gray-900 dark:text-white font-bold hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
              >
                Cancel
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowAddModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-dark-card rounded-lg p-8 max-w-md w-full shadow-xl"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {newCategory.parent !== "" ? "Add New Subcategory" : "Add New Category"}
            </h2>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  {newCategory.parent !== "" ? "Subcategory Name" : "Category Name"}
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:border-blue-500 transition-all font-bold"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder={newCategory.parent !== "" ? "e.g., Leafy Vegetables" : "e.g., Vegetables"}
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Parent Category (Optional)</label>
                <select
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:outline-none focus:border-blue-500 transition-all font-bold"
                  value={newCategory.parent}
                  onChange={(e) => setNewCategory({ ...newCategory, parent: e.target.value })}
                >
                  <option value="">None (Main Category)</option>
                  {categories.filter(c => !c.parent).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-400 mt-2 font-bold px-1 uppercase tracking-wider">Tip: Select a main category to create a sub-item</p>
              </div>
            </div>
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={addCategory}
                className="flex-1 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-bold transition-colors"
              >
                {newCategory.parent !== "" ? "Add Subcategory" : "Add Category"}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddModal(false)}
                className="flex-1 py-3 rounded-lg border-2 border-gray-300 dark:border-dark-border text-gray-900 dark:text-white font-bold hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
              >
                Cancel
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      {categoryToDelete && (() => {
        const subCatProductCount = categories
          .filter(c => String(c.parent?.id || c.parent) === String(categoryToDelete.id))
          .reduce((sum, sc) => sum + (sc.product_count || 0), 0);
        const totalProducts = (categoryToDelete.product_count || 0) + subCatProductCount;
        const hasProducts = totalProducts > 0;

        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setCategoryToDelete(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-card rounded-lg p-8 max-w-sm w-full shadow-xl text-center"
            >
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Delete Category?</h2>
              {hasProducts ? (
                <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/30 rounded-lg p-4 mb-6">
                  <p className="text-amber-800 dark:text-amber-400 text-sm font-medium">
                    This category cannot be deleted because it contains products. Please move or delete all products in this category before deleting it.
                  </p>
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Are you sure you want to delete this category? This action cannot be undone.
                </p>
              )}
              <div className="flex gap-3">
                <motion.button
                  whileHover={!hasProducts ? { scale: 1.05 } : {}}
                  whileTap={!hasProducts ? { scale: 0.95 } : {}}
                  disabled={hasProducts}
                  onClick={() => performDelete(categoryToDelete.id)}
                  className={`flex-1 py-3 rounded-lg font-bold transition-colors ${
                    hasProducts 
                    ? 'bg-gray-400 cursor-not-allowed text-white' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                >
                  {hasProducts ? "Blocked" : "Delete"}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCategoryToDelete(null)}
                  className="flex-1 py-3 rounded-lg border-2 border-gray-300 dark:border-dark-border text-gray-900 dark:text-white font-bold hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        );
      })()}
    </div>
  );
}
